import { Component, inject, Output, EventEmitter, signal, ChangeDetectorRef } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { FormGroup, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { SelectOptions } from '../../../../core/interfaces/select-options.interface';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { SignatureDialogComponent } from '../signature-dialog/signature-dialog.component';
import { PDFDocument } from 'pdf-lib';
import { NewRepApplicationService } from '../../new-rep-application/new-rep-application-services/new-rep-application.service';
import { HttpStatusCode } from '@angular/common/http';
import { SnackbarService } from '../../../../shared/services/snackbar/snackbar.service';
import { ConfirmPopupComponent } from '../../new-rep-application/confirm-popup/confirm-popup.component';
import { SessionStorageService } from '../../../../core/services/session-storage/session-storage.service';
import { IntroducerFormIndividualService } from './introducer-form-individual-service/introducer-form-individual-service.service';
import { Router } from '@angular/router';
import { UserAuthenticationService } from '../../../../core/services/user-authentication/user-authentication.service';
import { noWhitespace } from '../../../../shared/validators/no-whitespace.validator';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocumentPreviewOverlayDirective } from '../../../../shared/directives/document-preview-overlay/document-preview-overlay.directive';

export const MY_FORMATS = {
    parse: {
        dateInput: 'DD/MM/YYYY',
    },
    display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'DD/MM/YYYY',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'app-introducer-form-individual',
    providers: [provideMomentDateAdapter(MY_FORMATS)],
    imports: [
        MatExpansionModule, MatIcon, ReactiveFormsModule,
        MatFormFieldModule, MatInputModule, MatRadioModule, MatDatepickerModule,
        MatNativeDateModule, MatButtonModule, MatOptionModule, MatSelectModule,
        MatCheckboxModule, CommonModule, MatTooltipModule,
        DocumentPreviewOverlayDirective
    ],
    templateUrl: './introducer-form-individual.component.html',
    styleUrl: './introducer-form-individual.component.scss'
})
export class IntroducerFormIndividualComponent {
    @Output() next = new EventEmitter<boolean>();

    private fb = inject(FormBuilder);
    private newRepApplicationService = inject(NewRepApplicationService);
    private dialog = inject(MatDialog);
    private snackBarService = inject(SnackbarService);
    private sessionStorageService = inject(SessionStorageService);
    private introducerFormIndividualService = inject(IntroducerFormIndividualService);
    private cdr = inject(ChangeDetectorRef);
    private sanitizer = inject(DomSanitizer);
    public message = 'This field is required';
    formDisabled = false;

    private readonly MAX_PHONE_LENGTH = 13;
    private readonly ALLOWED_FILE_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);
    private readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

    countries!: SelectOptions[];
    nationalities!: SelectOptions[];
    viewIntroducerDetails!: any[];
    private applicationId!: number;
    private userRole: string | null = null;
    isReadOnly = signal(false);
    setSprinner = signal(false);   // ADD THIS
    // near your other private fields
    private employmentHistoryId = 0;
    // inside the class
    private router = inject(Router);
    private userAuthenticationService = inject(UserAuthenticationService);
    // Tracks the previous value of the "nationality" control so the
    // valueChanges subscriber can tell when the user is actually navigating
    // AWAY FROM "others" (as opposed to the initial load, or switching
    // between other non-"others" values) - only that transition should
    // trigger the address-proof delete flow.
    private previousNationality: string | null = null;

    // Raw Checklist array from the last GET response, keyed by categoryName ->
    // backend row id. Needed because buildChecklistPayload() must send the
    // real id for a previously-saved row (not 0) or the backend inserts a
    // new row every save instead of updating the existing one in place.
    private loadedChecklist: any[] = [];

    /** Prefix used for the dynamically added "Other Attachments" controls. */
    readonly otherAttachmentPrefix = 'otherAttachment';
    /** Document type sent for every "Other Attachments" upload - the row index goes in ResponseValue. */
    readonly otherAttachmentDescription = 'OtherAttachments';
    /** Control keys of the rendered "Other Attachments" rows, e.g. ['otherAttachment1', 'otherAttachment2']. */
    otherAttachmentKeys: string[] = [];
    private otherAttachmentSeq = 0;

    // ---------- Checklist category_name mapping (form control <-> API name) ----------
    private readonly CHECKLIST_CATEGORY_MAP: { [formControlName: string]: string } = {
        introducerQuestionnaires_1: 'generalInformation_ii_1',
        introducerQuestionnaires_2: 'generalInformation_ii_2',
        introducerQuestionnaires_3: 'generalInformation_ii_3',
        introducerQuestionnaires_4: 'generalInformation_ii_4',
        introducerQuestionnaires_5: 'generalInformation_ii_5',
        introducerQuestionnaires_6: 'generalInformation_ii_6',
        introducerQuestionnaires_7: 'generalInformation_ii_7',

        introducerQuestionnaires_8: 'fitPropercriteria_ii_1',
        introducerQuestionnaires_9: 'fitPropercriteria_ii_2',
        introducerQuestionnaires_10: 'fitPropercriteria_ii_3',
        introducerQuestionnaires_11: 'fitPropercriteria_ii_4',
        introducerQuestionnaires_12: 'fitPropercriteria_ii_5',
        introducerQuestionnaires_13: 'fitPropercriteria_ii_6',
        introducerQuestionnaires_14: 'fitPropercriteria_ii_7',
        introducerQuestionnaires_15: 'fitPropercriteria_ii_8',
        introducerQuestionnaires_16: 'fitPropercriteria_ii_9',
        introducerQuestionnaires_17: 'fitPropercriteria_ii_10',
        introducerQuestionnaires_18: 'fitPropercriteria_ii_11',
        introducerQuestionnaires_19: 'fitPropercriteria_ii_12',
        introducerQuestionnaires_20: 'fitPropercriteria_ii_13',
        introducerQuestionnaires_21: 'fitPropercriteria_ii_14',
        introducerQuestionnaires_22: 'fitPropercriteria_ii_15',
        introducerQuestionnaires_23: 'fitPropercriteria_ii_16',
        introducerQuestionnaires_24: 'fitPropercriteria_ii_17',
        introducerQuestionnaires_25: 'fitPropercriteria_ii_18',
        introducerQuestionnaires_26: 'fitPropercriteria_ii_19',
        introducerQuestionnaires_27: 'fitPropercriteria_ii_20',
        introducerQuestionnaires_28: 'fitPropercriteria_ii_21',
        introducerQuestionnaires_29: 'fitPropercriteria_ii_22',
        introducerQuestionnaires_30: 'fitPropercriteria_ii_23',
        introducerQuestionnaires_31: 'fitPropercriteria_ii_24',
        introducerQuestionnaires_32: 'fitPropercriteria_ii_25',
        introducerQuestionnaires_33: 'fitPropercriteria_ii_26',
        introducerQuestionnaires_34: 'fitPropercriteria_ii_27',
        introducerQuestionnaires_35: 'fitPropercriteria_ii_28',
        introducerQuestionnaires_36: 'fitPropercriteria_ii_29',
        introducerQuestionnaires_37: 'fitPropercriteria_ii_30',
    };
    // add next to CHECKLIST_CATEGORY_MAP
    private readonly FITPROPER_SUBQUESTION_CATEGORY_MAP: { [questionIndex: number]: string } = {
        8: 'fitPropercriteria_ii_yes_1',
        31: 'fitPropercriteria_ii_yes_24'
    };

    private readonly REVERSE_FITPROPER_SUBQUESTION_CATEGORY_MAP: { [apiCategoryName: string]: number } =
        Object.fromEntries(
            Object.entries(this.FITPROPER_SUBQUESTION_CATEGORY_MAP).map(([index, apiName]) => [apiName, Number(index)])
        );

    private readonly REVERSE_CHECKLIST_CATEGORY_MAP: { [apiCategoryName: string]: string } =
        Object.fromEntries(
            Object.entries(this.CHECKLIST_CATEGORY_MAP).map(([formControlName, apiName]) => [apiName, formControlName])
        );

    private readonly DOCUMENT_TYPE_LABELS: { [fileVar: string]: string } = {
        attachment_nric: 'Attachment (NRIC / Passport)',
        address_proof_attachment: 'Attachment (Address Proof)',
        attachment_employee: 'Attachment (Proof of employment)',
        payment_attachmentFile: 'Attachment (Bank Account Statement within last 3 months)',
        gendralinformation_introducer_Questionnaires_file_4: 'gendralinformation_introducer_Questionnaires_file_4'
    };

    // Reverse of the pairing above, plus the "display name" control for each
    // attachment - used to patch already-uploaded file names into the form
    // on load so the UI shows them instead of "Choose file".
    private readonly DOCUMENT_TYPE_CONTROL_MAP: { [categoryName: string]: { controlName: string; fileVar: string } } = {
        'Attachment (NRIC / Passport)': { controlName: 'nricattachmentfileName', fileVar: 'attachment_nric' },
        'Attachment (Address Proof)': { controlName: 'address_proof_attachmentfileName', fileVar: 'address_proof_attachment' },
        'Attachment (Proof of employment)': { controlName: 'employeeattachment', fileVar: 'attachment_employee' },
        'Attachment (Bank Account Statement within last 3 months)': { controlName: 'paymentattachment', fileVar: 'payment_attachmentFile' },
        'gendralinformation_introducer_Questionnaires_file_4': { controlName: 'introducerQuestionnairesfile_4', fileVar: 'gendralinformation_introducer_Questionnaires_file_4' }
    };

    public formGroup: FormGroup = this.fb.group({
        fullName: ['', [Validators.required, noWhitespace]],
        preferredName: [''],
        nric: ['', [Validators.required, noWhitespace]],
        nricattachmentfileName: ['', [Validators.required, noWhitespace]],
        attachment_nric: [''],
        attachment_additional: [''],
        gender: ['', Validators.required],
        dob: ['', Validators.required],
        manager: [''],
        nationality: ['', Validators.required],
        otherNationality: [''],
        date: [''],

        declarationName: [''],
        address: ['', [Validators.required, noWhitespace]],
        postalCode: ['', [Validators.required, noWhitespace]],

        homenumber: ['', [Validators.required, Validators.pattern(/^\+?[0-9]{1,13}$/), Validators.maxLength(13)]],
        mobilenumber: ['', [Validators.required, Validators.pattern(/^\+?[0-9]{1,13}$/), Validators.maxLength(13)]],

        signature: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        occupation: ['', [Validators.required, noWhitespace]],
        designation: ['', [Validators.required, noWhitespace]],
        currentEmployer: ['', [Validators.required, noWhitespace]],
        employerAddress: ['', [Validators.required, noWhitespace]],
        buisness: ['', [Validators.required, noWhitespace]],

        employeeattachment: ['', Validators.required],
        attachment_employee: [''],

        address_proof_attachmentfileName: [''],
        address_proof_attachment: [''],
        additionalInfo: [''],

        payee: ['', [Validators.required, noWhitespace]],
        acNo: ['', [Validators.required, noWhitespace]],
        bankName: ['', [Validators.required, noWhitespace]],
        branchCode: ['', [Validators.required, noWhitespace]],

        paymentattachment: ['', Validators.required],
        payment_attachmentFile: [''],

        additionalattachment: ['', Validators.required],

        introducerQuestionnaires_1: [null, Validators.required],
        introducerQuestionnairesyes_1: ['', [Validators.required, noWhitespace]],
        introducerQuestionnaires_2: [null, Validators.required],
        introducerQuestionnaires_3: [null, Validators.required],
        introducerQuestionnairesyes_3: ['', [Validators.required, noWhitespace]],
        introducerQuestionnaires_4: [null, Validators.required],

        introducerQuestionnairesfile_4: ['', Validators.required],
        gendralinformation_introducer_Questionnaires_file_4: [null],

        introducerQuestionnairesyes_4: [null, Validators.required],
        introducerQuestionnaires_5: [null, Validators.required],
        introducerQuestionnairesyes_5: ['', [Validators.required, noWhitespace]],
        introducerQuestionnaires_6: [null, Validators.required],
        introducerQuestionnairesyes_6: ['', [Validators.required, noWhitespace]],
        introducerQuestionnaires_7: [null, Validators.required],

        introducerQuestionnaires_8: [null, Validators.required],
        introducerQuestionnairesyes_8: ['', [Validators.required, noWhitespace]],
        introducerQuestionnairesop1_8: [null, Validators.required],
        introducerQuestionnairesop2_8: [null, Validators.required],

        introducerQuestionnaires_9: [null, Validators.required],
        introducerQuestionnaires_10: [null, Validators.required],
        introducerQuestionnaires_11: [null, Validators.required],
        introducerQuestionnaires_12: [null, Validators.required],
        introducerQuestionnaires_13: [null, Validators.required],
        introducerQuestionnaires_14: [null, Validators.required],
        introducerQuestionnaires_15: [null, Validators.required],
        introducerQuestionnaires_16: [null, Validators.required],
        introducerQuestionnaires_17: [null, Validators.required],
        introducerQuestionnaires_18: [null, Validators.required],
        introducerQuestionnaires_19: [null, Validators.required],
        introducerQuestionnaires_20: [null, Validators.required],
        introducerQuestionnaires_21: [null, Validators.required],
        introducerQuestionnaires_22: [null, Validators.required],
        introducerQuestionnaires_23: [null, Validators.required],
        introducerQuestionnaires_24: [null, Validators.required],
        introducerQuestionnaires_25: [null, Validators.required],
        introducerQuestionnaires_26: [null, Validators.required],
        introducerQuestionnaires_27: [null, Validators.required],
        introducerQuestionnaires_28: [null, Validators.required],
        introducerQuestionnaires_29: [null, Validators.required],
        introducerQuestionnaires_30: [null, Validators.required],

        introducerQuestionnaires_31: [null, Validators.required],
        introducerQuestionnairesop1_31: [null, Validators.required],
        introducerQuestionnairesop2_31: [null, Validators.required],

        introducerQuestionnaires_32: [null, Validators.required],
        introducerQuestionnaires_33: [null, Validators.required],
        introducerQuestionnaires_34: [null, Validators.required],
        introducerQuestionnaires_35: [null, Validators.required],
        introducerQuestionnaires_36: [null, Validators.required],
        introducerQuestionnaires_37: [null, Validators.required],

        declarationaccepted: ['']
    });

    // ---------- Section / panel state ----------

    private sectionSavedState: { [key: string]: boolean } = {
        introducer: false, employment: false, payment: false,
        general: false, fitproper: false, additional: false, declaration: false
    };


    private sectionFieldsMap: { [key: string]: string[] } = {
        introducer: [
            'fullName', 'preferredName', 'nric', 'nricattachmentfileName', 'gender', 'dob',
            'nationality', 'otherNationality', 'address',
            'address_proof_attachmentfileName', 'homenumber', 'mobilenumber', 'email'
        ],
        employment: [
            'occupation', 'designation', 'currentEmployer',
            'employerAddress', 'buisness', 'employeeattachment'
        ],
        payment: ['payee', 'acNo', 'bankName', 'branchCode', 'paymentattachment'],
        general: [
            'introducerQuestionnaires_1', 'introducerQuestionnairesyes_1',
            'introducerQuestionnaires_2', 'introducerQuestionnaires_3',
            'introducerQuestionnairesyes_3', 'introducerQuestionnaires_4',
            'introducerQuestionnairesfile_4', 'introducerQuestionnaires_5',
            'introducerQuestionnairesyes_5', 'introducerQuestionnaires_6',
            'introducerQuestionnairesyes_6', 'introducerQuestionnaires_7'
        ],
        fitproper: [
            'introducerQuestionnaires_8', 'introducerQuestionnairesyes_8',
            'introducerQuestionnairesop1_8', 'introducerQuestionnairesop2_8',
            'introducerQuestionnaires_9', 'introducerQuestionnaires_10',
            'introducerQuestionnaires_11', 'introducerQuestionnaires_12',
            'introducerQuestionnaires_13', 'introducerQuestionnaires_14',
            'introducerQuestionnaires_15', 'introducerQuestionnaires_16',
            'introducerQuestionnaires_17', 'introducerQuestionnaires_18',
            'introducerQuestionnaires_19', 'introducerQuestionnaires_20',
            'introducerQuestionnaires_21', 'introducerQuestionnaires_22',
            'introducerQuestionnaires_23', 'introducerQuestionnaires_24',
            'introducerQuestionnaires_25', 'introducerQuestionnaires_26',
            'introducerQuestionnaires_27', 'introducerQuestionnaires_28',
            'introducerQuestionnaires_29', 'introducerQuestionnaires_30',
            'introducerQuestionnaires_31', 'introducerQuestionnairesop1_31',
            'introducerQuestionnairesop2_31', 'introducerQuestionnaires_32',
            'introducerQuestionnaires_33', 'introducerQuestionnaires_34',
            'introducerQuestionnaires_35', 'introducerQuestionnaires_36',
            'introducerQuestionnaires_37'
        ],
        additional: ['additionalInfo'],
        declaration: ['declarationaccepted', 'date']
    };

    sectionOrder: string[] = [
        'introducer', 'employment', 'payment', 'general', 'fitproper', 'additional', 'declaration'
    ];

    activePanelIndex = -1;
    private detailsLoaded = false;
    data = history.state;

    selectedDocument!: SafeResourceUrl;
    isPdf = false;

    readonly MIN_ZOOM = 0.5;
    readonly MAX_ZOOM = 3;
    private readonly ZOOM_STEP = 0.25;
    zoomLevel = 1;
    showPreview = false;
    currentFileName!: string;

    constructor() {
        this.setupNationalityValidators();
        this.setupAutoUnsaveOnChange();
    }

    // async ngOnInit(): Promise<void> {
    //     this.userRole = String(this.sessionStorageService.getCurrentUserRole());
    //     // console.log('userRole:', this.userRole);

    //     const dropdownPromise = this.getDropdownOptions();
    //     let detailsPromise: Promise<void>;

    //     if (this.userRole == 'Introducer - Individual') {
    //         this.applicationId = Number(this.sessionStorageService.getCurrentUserId());
    //         // console.log('applicationId set to:', this.applicationId, 'raw value:', this.sessionStorageService.getCurrentUserId());
    //         detailsPromise = this.loadIntroducerApplicationDetails();
    //     } else {
    //         this.applicationId = this.data.id;
    //         detailsPromise = this.loadIntroducerApplicationDetails();
    //         this.isReadOnly.set(true);
    //         this.isReadOnly() ? this.formGroup?.disable() : this.formGroup?.enable();
    //     }

    //     await Promise.all([dropdownPromise, detailsPromise]);
    //     this.reconcileDropdownSelections();
    //     this.refreshAllSectionStatuses();
    //     this.detailsLoaded = true;
    // }
    async ngOnInit(): Promise<void> {
        this.setSprinner.set(true);
        this.userRole = String(this.sessionStorageService.getCurrentUserRole());

        const dropdownPromise = this.getDropdownOptions();
        let detailsPromise: Promise<void>;
        const isOtherRole = this.userRole !== 'Introducer - Individual';

        if (!isOtherRole) {
            this.applicationId = Number(this.sessionStorageService.getCurrentUserId());
            detailsPromise = this.loadIntroducerApplicationDetails();
        } else {
            this.applicationId = this.data.id;
            detailsPromise = this.loadIntroducerApplicationDetails();
            // don't disable yet — wait until status is computed
        }

        await Promise.all([dropdownPromise, detailsPromise]);
        this.reconcileDropdownSelections();

        // Compute + freeze statuses WHILE the form is still enabled,
        // so isSectionValid() sees real control.valid results.
        this.refreshAllSectionStatuses();

        // NOW it's safe to disable — status is already frozen in the signal.
        if (isOtherRole || this.isReadOnly()) {
            this.isReadOnly.set(true);
            this.formGroup.disable();
        }

        this.detailsLoaded = true;
    }

    private reconcileDropdownSelections(): void {
        const otherNationalityControl = this.formGroup.get('otherNationality');
        const currentValue = otherNationalityControl?.value;

        if (currentValue && Array.isArray(this.nationalities)) {
            const match = this.nationalities.find(
                n => String(n.value).toLowerCase() === String(currentValue).toLowerCase()
            );
            if (match && match.value !== currentValue) {
                otherNationalityControl?.setValue(match.value, { emitEvent: false });
            }
        }

        // If we landed on "Singapore Citizen" before the nationalities list was
        // ready, getDefaultCitizenNationalityValue() fell back to the literal
        // string 'Singapore' - re-resolve it now against the real dropdown data.
        if (this.formGroup.get('nationality')?.value === 'Singapore Citizen' && currentValue === 'Singapore') {
            otherNationalityControl?.setValue(this.getDefaultCitizenNationalityValue(), { emitEvent: false });
        }
    }
    private showSubmittedAndLogout(): void {
        const dialogRef = this.dialog.open(ConfirmPopupComponent, {
            width: '500px',
            disableClose: true,
            data: {
                message: 'Your Application has been successfully submitted. Currently in Review. No further action required.',
                hidecancel: true,
            }
        });

        dialogRef.afterClosed().subscribe(async (result) => {
            if (result?.data === true) {
                const responseData = await this.userAuthenticationService.userLogout()
                    .then((response) => JSON.parse(response))
                    .catch((error) => error);

                if (responseData?.status === HttpStatusCode.Ok) {
                    this.snackBarService.openSuccessSnackBar('Logged out successfully!');
                } else {
                    this.snackBarService.openFailureSnackBar('We couldn\'t process your request. Please try again');
                }

                this.sessionStorageService.clearSessionStorage();
                this.router.navigate(['/login']);
            }
        });
    }
    async loadIntroducerApplicationDetails(): Promise<void> {
        // --- fix 1: log what id is actually being sent ---
        // console.log('Fetching details for applicationId:', this.applicationId, typeof this.applicationId);

        // --- fix 2: guard against invalid id before calling the API ---
        if (!this.applicationId || isNaN(this.applicationId)) {
            console.warn('Skipping load — invalid applicationId:', this.applicationId);
            return;
        }
        const rawResponse = await this.introducerFormIndividualService
            .getIntroducerApplicationDetails(this.applicationId)
            .catch((error) => {
                console.error('Failed to load introducer application details', error);
                return null;
            });

        if (!rawResponse) {
            return;
        }

        let responseBody: any = rawResponse;
        if (typeof rawResponse === 'string') {
            try {
                responseBody = JSON.parse(rawResponse);
            } catch (e) {
                console.error('Failed to parse introducer application details response', e);
                return;
            }
        }
        this.setSprinner.set(false);
        const body = responseBody?.data ?? responseBody;

        const data = body?.responsedata?.IntroducerData;
        if (!data) {
            console.warn('No IntroducerData found in response:', body);
            return;
        }

        if (!['Candidate Submission', 'Candidate Update (Due Diligence)', 'Candidate Update (Assessment)'].includes(data.status)) {
            this.isReadOnly.set(true);

            // if (this.userRole === 'Introducer - Individual') {
            //     this.showSubmittedAndLogout();
            // }
        } else {
            // Candidate Submission → editable ONLY for the applicant themselves
            if (this.userRole === 'Introducer - Individual') {
                this.isReadOnly.set(false);
            } else {
                this.isReadOnly.set(true);
            }
        }


        const { nationality, otherNationality } = this.parseNationalityValue(data.nationality ?? '');

        // Seed previousNationality with the loaded value so the very first
        // change the user makes is evaluated against the real starting
        // point, not null.
        this.previousNationality = nationality;

        const employmentHistory = this.getLatestEmploymentHistory(body?.responsedata?.employment_history);
        this.employmentHistoryId = employmentHistory?.id ?? 0;


        this.patchIfPristine({
            fullName: data.fullNameNric ?? '',
            preferredName: data.preferedName ?? '',
            nric: data.nricNo ?? '',
            dob: this.isZeroDate(data.dateOfBirth) ? null : new Date(data.dateOfBirth),
            gender: data.gender ? String(data.gender).toLowerCase() : '',
            nationality: nationality,
            otherNationality: otherNationality,
            address: data.address ?? '',
            homenumber: data.homeNumber ?? '',
            mobilenumber: data.mobileNumber ?? '',
            email: data.personalEmailAddress ?? '',

            payee: data.nameOfPayee ?? '',

            bankName: data.bankname ?? '',
            acNo: !this.isPlaceholderBankValue(data.bankAccountNumber) ? data.bankAccountNumber : '',
            branchCode: !this.isPlaceholderBankValue(data.bankBranchName) ? data.bankBranchName : '',

            additionalInfo: data.declarationComment ?? '',
            date: data.isAgreed === true && data.declarationDate
                ? this.toDateInputValue(data.declarationDate)
                : '',
            declarationName: data.isAgreed === true
                ? (data.fullNameNric ?? '')
                : '',
            declarationaccepted: data.isAgreed === true,

            // employment fields come from body.responsedata.employment_history, not IntroducerData
            occupation: employmentHistory?.occupation ?? '',
            designation: employmentHistory?.designation ?? '',
            currentEmployer: employmentHistory?.currentEmployer ?? '',
            employerAddress: employmentHistory?.employerAddress ?? '',
            buisness: employmentHistory?.natureOfBusiness ?? ''
        });

        if (this.formGroup.get('nationality')?.value === 'Singapore Citizen') {
            const otherNationalityControl = this.formGroup.get('otherNationality');
            if (!otherNationalityControl?.value) {
                otherNationalityControl?.setValue(this.getDefaultCitizenNationalityValue(), { emitEvent: false });
            }
            otherNationalityControl?.disable({ emitEvent: false });
        }


        this.viewIntroducerDetails = body.responsedata.attachments ?? [];
        this.patchAttachments(this.viewIntroducerDetails);
        this.buildOtherAttachments();

        this.loadedChecklist = body.responsedata.Checklist ?? [];
        this.patchChecklist(this.loadedChecklist);

        this.syncConditionalFileValidator();
        this.recomputeSectionCompletionFromServerData();

        this.debugSectionValidity('introducer');
    }

    private isPlaceholderBankValue(value: string | undefined | null): boolean {
        if (!value) return true;
        const normalized = value.trim().toUpperCase();
        return normalized === 'PENDING' || /^0+$/.test(normalized);
    }
    /**
     * Lightweight re-fetch used after a general/fitproper save purely to
     * refresh loadedChecklist's ids - so getChecklistItemId() has the
     * backend-assigned id for any row that was just created for the first
     * time (previously sent as id: 0). Does NOT touch form values, so it
     * can't clobber anything the user is mid-editing.
     */
    private async refreshChecklistIds(): Promise<void> {
        const rawResponse = await this.introducerFormIndividualService
            .getIntroducerApplicationDetails(this.applicationId)
            .catch((error) => {
                console.error('Failed to refresh checklist ids', error);
                return null;
            });

        if (!rawResponse) {
            return;
        }

        let responseBody: any = rawResponse;
        if (typeof rawResponse === 'string') {
            try {
                responseBody = JSON.parse(rawResponse);
            } catch (e) {
                console.error('Failed to parse checklist-refresh response', e);
                return;
            }
        }

        const body = responseBody?.data ?? responseBody;
        this.loadedChecklist = body?.responsedata?.Checklist ?? this.loadedChecklist;
    }

    /**
     * Upload's response only returns the filename (no onboardingId/docTypeId/id),
     * so viewIntroducerDetails can't be updated locally after upload. Re-fetch
     * attachments from the server so getLatestAttachmentByType() has the real
     * ids immediately - without this, deleting a file in the same session
     * (before a page refresh) sends undefined ids to deletedocument() and fails.
     */
    private async refreshAttachments(): Promise<void> {
        const rawResponse = await this.introducerFormIndividualService
            .getIntroducerApplicationDetails(this.applicationId)
            .catch((error) => {
                console.error('Failed to refresh attachments', error);
                return null;
            });

        if (!rawResponse) {
            return;
        }

        let responseBody: any = rawResponse;
        if (typeof rawResponse === 'string') {
            try {
                responseBody = JSON.parse(rawResponse);
            } catch (e) {
                console.error('Failed to parse attachments-refresh response', e);
                return;
            }
        }

        const body = responseBody?.data ?? responseBody;
        this.viewIntroducerDetails = body?.responsedata?.attachments ?? this.viewIntroducerDetails;
        // Picks up the ids for a just-uploaded "Other Attachment" so removing it
        // in the same session has real ids to send to deletedocument().
        this.buildOtherAttachments();
    }

    /**
     * After patching data back from the server, mark any section whose
     * required fields are already validly filled as "completed". Checking
     * isSectionValid() alone isn't enough — some sections (e.g. "additional")
     * have no required fields and are trivially "valid" even when empty, which
     * would incorrectly flip them to "completed" instead of "notstarted".
     * So a section is only promoted to completed if it's valid AND has at
     * least one field with an actual value.
     */
    private recomputeSectionCompletionFromServerData(): void {
        Object.keys(this.sectionFieldsMap).forEach(sectionKey => {
            const fields = this.sectionFieldsMap[sectionKey];
            const anyFilled = fields.some(name => {
                const control = this.formGroup.get(name);
                return control && this.isFieldFilled(control.value);
            });

            if (anyFilled && this.isSectionValid(sectionKey)) {
                this.sectionSavedState[sectionKey] = true;
            }
        });

        // Declaration stays special-cased: "completed" only if actually accepted.
        this.sectionSavedState['declaration'] = this.isDeclarationAccepted();
    }
    // ---------- Other Attachments ----------

    /**
     * Restores any previously uploaded "Other Attachments" and always keeps at
     * least one empty row. Safe to call repeatedly (after every attachments
     * re-fetch) - restoreOtherAttachment() only adds controls it hasn't seen.
     */
    private buildOtherAttachments(): void {
        this.viewIntroducerDetails?.forEach(item => {
            const index = this.otherAttachmentIndexOf(item);
            if (index !== null) {
                this.restoreOtherAttachment(`${this.otherAttachmentPrefix}${index}`, item.fileName);
            }
        });

        if (this.otherAttachmentKeys.length === 0) {
            this.addOtherAttachment();
        }

        // The API lists the documents in its own order, so the rows are put back in
        // row order here. Left unsorted the fields swap places on every fetch, and
        // because the template hangs the remove button off every row but the first
        // and the add button off the last, those buttons end up on whichever rows
        // came back first.
        this.otherAttachmentKeys.sort((a, b) => this.otherAttachmentRow(a) - this.otherAttachmentRow(b));
    }

    /** True when the category name is one of the "Other Attachments" control keys. */
    private isOtherAttachmentKey(categoryName: string): boolean {
        return categoryName.startsWith(this.otherAttachmentPrefix);
    }

    /** The row index sent as ResponseValue for an "Other Attachments" control key. */
    private otherAttachmentIndex(key: string): string {
        return key.slice(this.otherAttachmentPrefix.length);
    }

    /** Row index of a control key as a number, for matching and ordering. */
    private otherAttachmentRow(key: string): number {
        return Number(this.otherAttachmentIndex(key));
    }

    /**
     * Row index an uploaded document belongs to, or null when it isn't an "Other
     * Attachments" row. Uploads carry 'OtherAttachments' as the document type
     * with the index in responseValue; rows saved before that change kept the
     * index in the document type itself, so both shapes are recognised here. The
     * index is read as a number so a row is still matched when the API echoes it
     * back in a different form (' 2', 2) than the one that was sent.
     */
    private otherAttachmentIndexOf(item: any): number | null {
        const docType: string = item?.onboardingDocumentType ?? '';
        const raw = docType === this.otherAttachmentDescription
            ? String(item?.responseValue ?? '').trim()
            : docType.startsWith(this.otherAttachmentPrefix)
                ? docType.slice(this.otherAttachmentPrefix.length)
                : '';

        const row = Number(raw);
        return raw !== '' && Number.isFinite(row) ? row : null;
    }

    private restoreOtherAttachment(key: string, fileName: string): void {
        if (!this.otherAttachmentKeys.includes(key)) {
            this.formGroup.addControl(key, this.fb.control([null]));
            this.formGroup.addControl(`${key}fileName`, this.fb.control(''));
            this.otherAttachmentKeys.push(key);

            const seq = Number(key.slice(this.otherAttachmentPrefix.length));
            if (!Number.isNaN(seq) && seq > this.otherAttachmentSeq) {
                this.otherAttachmentSeq = seq;
            }
        }
        this.formGroup.get(`${key}fileName`)?.setValue(fileName);
    }

    /** Duplicates the "Other Attachments" field so another file can be attached. */
    addOtherAttachment(): void {
        const key = `${this.otherAttachmentPrefix}${++this.otherAttachmentSeq}`;
        this.formGroup.addControl(key, this.fb.control([null]));
        this.formGroup.addControl(`${key}fileName`, this.fb.control(''));
        this.otherAttachmentKeys.push(key);
    }

    /** Removes a duplicated "Other Attachments" row, deleting its uploaded file first when there is one. */
    removeOtherAttachment(key: string): void {
        if (!this.formGroup.get(`${key}fileName`)?.value) {
            this.dropOtherAttachment(key);
            return;
        }

        const dialogRef = this.dialog.open(ConfirmPopupComponent, {
            width: '500px',
            disableClose: true,
            data: {
                message: 'Do you want to remove the attachement?',
                hidecancel: false,
            }
        });

        dialogRef.afterClosed().subscribe(async result => {
            if (result?.data !== true) {
                return;
            }

            const match = this.getLatestAttachmentByType(key);
            if (match) {
                const { onboardingId, docTypeId, id } = match;
                const responseData = await this.newRepApplicationService
                    .deletedocument({ onboardingId, docTypeId, id })
                    .then((response) => JSON.parse(response))
                    .catch((error) => {
                        console.error('Failed to delete other attachment', error);
                        return null;
                    });

                // Same rule as removeFile(): only clear the row once the backend
                // confirms, otherwise the file would reappear on the next load.
                if (responseData?.status !== HttpStatusCode.Ok) {
                    this.snackBarService.openFailureSnackBar('We couldn\'t remove the attachment. Please try again');
                    return;
                }

                this.viewIntroducerDetails = this.viewIntroducerDetails.filter(item => item !== match);
            }

            this.dropOtherAttachment(key);
        });
    }

    private dropOtherAttachment(key: string): void {
        this.formGroup.removeControl(key);
        this.formGroup.removeControl(`${key}fileName`);
        this.otherAttachmentKeys = this.otherAttachmentKeys.filter(item => item !== key);
    }

    /**
     * Returns the most recently uploaded attachment for a given
     * onboardingDocumentType from the cached viewIntroducerDetails list.
     * "Other Attachments" rows all share one document type, so those are
     * matched on the row index carried in responseValue instead.
     * Shared by patchAttachments() (decides what the UI displays) and
     * removeFile() (decides what gets deleted) so they always agree on
     * which physical file is "the current one" for that document type.
     */
    private getLatestAttachmentByType(categoryName: string): any | undefined {
        if (!Array.isArray(this.viewIntroducerDetails)) {
            return undefined;
        }
        const matches = this.isOtherAttachmentKey(categoryName)
            ? this.viewIntroducerDetails.filter(item => this.otherAttachmentIndexOf(item) === this.otherAttachmentRow(categoryName))
            : this.viewIntroducerDetails.filter(item => item?.onboardingDocumentType === categoryName);

        return matches
            .reduce((latest: any, item: any) => {
                if (!latest) return item;
                return new Date(item.uploadDate) > new Date(latest.uploadDate) ? item : latest;
            }, undefined);
    }
    private patchAttachments(attachments: any[]): void {
        if (!Array.isArray(attachments) || attachments.length === 0) {
            return;
        }

        const categoryNames = new Set(attachments.map(item => item?.onboardingDocumentType).filter(Boolean));

        const patch: { [key: string]: any } = {};
        categoryNames.forEach(categoryName => {
            const mapping = this.DOCUMENT_TYPE_CONTROL_MAP[categoryName as string];
            if (!mapping) return;

            const item = this.getLatestAttachmentByType(categoryName as string);
            if (!item) return;

            const controlNameControl = this.formGroup.get(mapping.controlName);
            const fileVarControl = this.formGroup.get(mapping.fileVar);

            if (controlNameControl && controlNameControl.pristine) {
                patch[mapping.controlName] = item.fileName;
            }
            // No real File/Blob exists for an already-uploaded document - a
            // display-only stand-in with just `.name` is enough since the
            // template only ever reads `{{ control.value.name }}`.
            if (fileVarControl && fileVarControl.pristine) {
                patch[mapping.fileVar] = { name: item.fileName };
            }
        });

        this.formGroup.patchValue(patch);
    }

    private isZeroDate(value: string | undefined | null): boolean {
        if (!value) return true;
        return value.startsWith('0001-01-01') || value.startsWith('1900-01-01');
    }

    /**
     * Maps the GET response's Checklist array back onto the matching
     * introducerQuestionnaires_N / introducerQuestionnairesyes_N controls.
     * NOTE: the GET response uses camelCase field names (categoryName,
     * isCompleted, responseValue) - different from the snake_case shape
     * (category_name, is_completed, response_value) sent on POST/save.
     * item.categoryName arrives using the RENAMED API convention
     * (generalInformation_ii_N / fitPropercriteria_ii_N), so it's translated
     * back to the internal form control name via REVERSE_CHECKLIST_CATEGORY_MAP.
     */
    private patchChecklist(checklist: any[]): void {
        if (!Array.isArray(checklist) || checklist.length === 0) {
            return;
        }

        const patch: { [key: string]: any } = {};

        checklist.forEach(item => {
            const apiCategoryName = item?.categoryName;
            if (!apiCategoryName) return;

            // Handle the Q8 / Q31 paired sub-questions first — their category
            // names (fitPropercriteria_ii_yes_1 / _24) don't map onto a single
            // form control the way the rest of the checklist does; they map onto
            // one of two mutually-exclusive controls (op1 = Yes, op2 = No).
            const subQuestionIndex = this.REVERSE_FITPROPER_SUBQUESTION_CATEGORY_MAP[apiCategoryName];
            if (subQuestionIndex) {
                const op1ControlName = `introducerQuestionnairesop1_${subQuestionIndex}`;
                const op2ControlName = `introducerQuestionnairesop2_${subQuestionIndex}`;
                const op1Control = this.formGroup.get(op1ControlName);
                const op2Control = this.formGroup.get(op2ControlName);

                if (item.isCompleted === true && op1Control?.pristine) {
                    patch[op1ControlName] = true;
                } else if (item.isCompleted === false && op2Control?.pristine) {
                    patch[op2ControlName] = false;
                }
                return;
            }

            const formControlName = this.REVERSE_CHECKLIST_CATEGORY_MAP[apiCategoryName] ?? apiCategoryName;

            const control = this.formGroup.get(formControlName);
            if (control && control.pristine) {
                if (item.isCompleted === true || item.isCompleted === false) {
                    patch[formControlName] = item.isCompleted;
                }
            }

            const match = formControlName.match(/^introducerQuestionnaires_(\d+)$/);
            if (match) {
                const index = match[1];
                const followUpControlName = `introducerQuestionnairesyes_${index}`;
                const followUpControl = this.formGroup.get(followUpControlName);
                if (followUpControl && followUpControl.pristine && item.responseValue) {
                    patch[followUpControlName] = item.responseValue;
                }
            }
        });

        this.formGroup.patchValue(patch);
    }

    private toDateInputValue(isoDate: string): string {
        return new Date(isoDate).toISOString().split('T')[0];
    }

    isPanelExpanded(index: number): boolean {
        return this.activePanelIndex === index;
    }

    onPanelOpened(index: number): void {
        this.activePanelIndex = index;



        // if (index === 6) {
        //     const dateControl = this.formGroup.get('date');
        //     if (!dateControl?.value) {
        //         dateControl?.setValue(this.toDateInputValue(new Date().toISOString()));
        //     }
        // }
    }

    onPanelClosed(index: number): void {
        if (this.activePanelIndex === index) {
            this.activePanelIndex = -1;
        }
    }

    // NEW
    allSectionsCompletedExceptDeclaration(): boolean {
        const statuses = this.sectionStatuses();
        return Object.keys(this.sectionFieldsMap)
            .filter(key => key !== 'declaration')
            .every(key => statuses[key] === 'completed');
    }

    private patchIfPristine(patch: { [key: string]: any }): void {
        const safePatch: any = {};
        Object.keys(patch).forEach(key => {
            const control = this.formGroup.get(key);
            if (control && control.pristine) {
                safePatch[key] = patch[key];
            }
        });
        this.formGroup.patchValue(safePatch);
    }

    // ---------- API payload builders ----------

    private buildUpdatePayload(sectionKey: string): any {
        const v = this.formGroup.value;
        const payload: any = { id: this.applicationId, checklist: [] };

        switch (sectionKey) {
            case 'introducer':
                payload.personal_detail = {
                    full_name_nric: this.changeFullName(v.fullName),
                    preferred_name:  this.changeFullName(v.preferredName),
                    nric_no: v.nric,
                    date_of_birth: this.formatDateOrNull(v.dob),
                    gender: v.gender,
                    nationality: this.buildNationalityValue(v.nationality, v.otherNationality)
                };
                payload.address_full_details = {
                    address: v.address,
                    home_number: v.homenumber,
                    mobile_number: v.mobilenumber,
                    personal_email_address: v.email
                };
                break;

            case 'employment':
                payload.employment_detail = {
                    occupation: v.occupation,
                    designation: v.designation,
                    current_employer: v.currentEmployer,
                    employer_address: v.employerAddress,
                    nature_of_business: v.buisness
                };
                payload.employement_history = {
                    id: this.employmentHistoryId,
                    onboardingRepId: this.applicationId,
                    occupation: v.occupation,
                    lastPosition_held: v.designation,
                    current_employer: v.currentEmployer,
                    employer_address: v.employerAddress,
                    nature_of_business: v.buisness
                };
                break;

            case 'payment':
                payload.bank_detail = {
                    name_of_payee: v.payee,
                    bank_account_number: v.acNo,
                    bankname: v.bankName,
                    bank_branch_name: v.branchCode
                };
                break;

            case 'additional':
                payload.declaration_comment_detail = {
                    declaration_comment: v.additionalInfo
                };
                break;

            case 'declaration':
                payload.declaration_full_detail = {
                    declaration_full_name: v.declarationName,
                    declaration_date: this.formatDateOrNull(v.date),
                    isAgreed: v.declarationaccepted === true
                };
                break;

            case 'general':
            case 'fitproper':
                payload.checklist = this.buildChecklistPayload();
                break;
        }

        return payload;
    }

    private buildNationalityValue(nationality: string, otherNationality: string): string {
        if (nationality === 'Singapore Permanent Resident' || nationality === 'others') {
            return otherNationality ? `${nationality}, ${otherNationality}` : nationality;
        }
        return nationality;
    }

    private parseNationalityValue(raw: string): { nationality: string; otherNationality: string } {
        if (!raw) {
            return { nationality: '', otherNationality: '' };
        }
        const prefixes = ['Singapore Permanent Resident', 'others'];
        for (const prefix of prefixes) {
            if (raw.startsWith(prefix)) {
                const rest = raw.slice(prefix.length).replace(/^,\s*/, '');
                return { nationality: prefix, otherNationality: rest };
            }
        }
        return { nationality: raw, otherNationality: '' };
    }

    private formatDateOrNull(value: any): string | null {
        if (!value) {
            return null;
        }
        const date = value instanceof Date ? value : new Date(value);
        if (isNaN(date.getTime())) {
            return null;
        }

        // Build the date string from LOCAL Y-M-D components, not toISOString(),
        // which converts to UTC and can shift the date back a day for timezones
        // ahead of UTC (e.g. Singapore, UTC+8) - a local midnight selection like
        // 2000-05-15 00:00 +08:00 becomes 2000-05-14T16:00:00Z, sending the
        // wrong (earlier) date to the backend.
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}T00:00:00Z`;
    }

    /**
     * Looks up the backend-assigned id for a previously-saved checklist item
     * by its category_name, from the last GET response (loadedChecklist).
     * 0 means "never saved before" - the backend needs this distinction to
     * know whether to INSERT a new row or UPDATE the existing one; sending
     * 0 (or omitting id) on every save causes it to insert a new row each
     * time instead of updating the same row in place - which is why
     * previously-saved answers like isCompleted kept coming back null.
     */

    //  private getChecklistItemId(categoryName: string): number {
    //     const match = this.loadedChecklist.find(item => item?.categoryName === categoryName);
    //     return match?.id ?? 0;
    // }


    private getChecklistItemId(categoryName: string): number {
        const matches = this.loadedChecklist.filter(item => item?.categoryName === categoryName);
        if (matches.length === 0) return 0;
        const latest = matches.reduce((a, b) =>
            new Date(a.updatedAt) > new Date(b.updatedAt) ? a : b
        );
        return latest.id ?? 0;
    }

    private buildChecklistPayload(): any[] {
        const checklist: any[] = [];
        const v = this.formGroup.value;

        for (let i = 1; i <= 37; i++) {
            const key = `introducerQuestionnaires_${i}`;
            if (v[key] === undefined) continue;

            const categoryName = this.CHECKLIST_CATEGORY_MAP[key];
            if (!categoryName) continue;

            checklist.push({
                id: this.getChecklistItemId(categoryName),
                category_name: categoryName,
                is_completed: v[key],
                response_value: v[`introducerQuestionnairesyes_${i}`] ?? ''
            });

            // Q8 -> fitPropercriteria_ii_yes_1, Q31 -> fitPropercriteria_ii_yes_24
            const subCategoryName = this.FITPROPER_SUBQUESTION_CATEGORY_MAP[i];
            if (subCategoryName) {
                const op1 = v[`introducerQuestionnairesop1_${i}`];
                const op2 = v[`introducerQuestionnairesop2_${i}`];

                let subIsCompleted: boolean | null = null;
                if (op1 === true) subIsCompleted = true;
                else if (op2 === false) subIsCompleted = false;

                checklist.push({
                    id: this.getChecklistItemId(subCategoryName),
                    category_name: subCategoryName,
                    is_completed: subIsCompleted,
                    response_value: ''
                });
            }
        }

        return checklist;
    }

    private async saveSectionToApi(sectionKey: string, silent: boolean = false): Promise<void> {
        if (!this.applicationId || isNaN(this.applicationId)) {
            console.error('Cannot save - applicationId is invalid:', this.applicationId, 'section:', sectionKey);
            this.snackBarService.openFailureSnackBar('Unable to save - please refresh the page and try again');
            return;
        }
        const payload = this.buildUpdatePayload(sectionKey);

        const responseData = await this.introducerFormIndividualService
            .updateIntroducerApplicationDetails(payload)
            .then((response) => JSON.parse(response))
            .catch((error) => {
                console.error(`Failed to save section "${sectionKey}"`, error);
                return null;
            });

        if (responseData?.status === HttpStatusCode.Ok) {
            if (sectionKey === 'employment') {
                const returnedRaw = responseData?.data?.responsedata?.employment_history;
                const returned = this.getLatestEmploymentHistory(returnedRaw);
                if (returned?.id) {
                    this.employmentHistoryId = returned.id;
                }
            }

            if (sectionKey === 'general' || sectionKey === 'fitproper') {
                // Refresh loadedChecklist so any row that was just created for
                // the first time (previously id: 0) picks up its real backend
                // id - otherwise the *next* save would send id: 0 for it again
                // and create yet another duplicate row instead of updating it.
                await this.refreshChecklistIds();
            }

            if (!silent) {
                this.snackBarService.openSuccessSnackBar('Saved successfully!');
            }
        } else {
            this.snackBarService.openFailureSnackBar('We couldn\'t process your request. Please try again');
        }
    }

    // ---------- Validity (still used for the status badge only) ----------

    private isSectionValid(sectionKey: string): boolean {
        const fields = this.sectionFieldsMap[sectionKey] || [];

        if (sectionKey === 'introducer') {
            const nationality = this.formGroup.get('nationality')?.value;
            return fields.every(name => {
                const control = this.formGroup.get(name);
                if (!control) return true;
                if (name === 'otherNationality') {
                    if (nationality !== 'Singapore Permanent Resident' && nationality !== 'others') return true;
                    return control.valid;
                }
                if (name === 'address_proof_attachmentfileName') {
                    if (nationality !== 'others') return true;
                    return control.valid;
                }
                return control.valid;
            });
        }

        if (sectionKey === 'additional') {
            return true;

        }

        if (sectionKey === 'general') {
            const conditionalMap: { [key: string]: string } = {
                introducerQuestionnaires_1: 'introducerQuestionnairesyes_1',
                introducerQuestionnaires_3: 'introducerQuestionnairesyes_3',
                introducerQuestionnaires_4: 'introducerQuestionnairesfile_4',
                introducerQuestionnaires_5: 'introducerQuestionnairesyes_5',
                introducerQuestionnaires_6: 'introducerQuestionnairesyes_6'
            };

            return fields.every(name => {
                const control = this.formGroup.get(name);
                if (!control) return true;

                const parentEntry = Object.entries(conditionalMap).find(([, followUp]) => followUp === name);
                if (parentEntry) {
                    const parentControl = this.formGroup.get(parentEntry[0]);
                    if (parentControl?.value !== true) {
                        return true;
                    }
                    return control.valid;
                }

                return control.valid;
            });
        }

        if (sectionKey === 'fitproper') {
            return fields.every(name => {
                const control = this.formGroup.get(name);
                if (!control) return true;

                if (name === 'introducerQuestionnairesyes_8') {
                    return this.formGroup.get('introducerQuestionnaires_8')?.value !== true || control.valid;
                }
                if (name === 'introducerQuestionnairesop1_8' || name === 'introducerQuestionnairesop2_8') {
                    if (this.formGroup.get('introducerQuestionnaires_8')?.value !== true) return true;
                    const op1 = this.formGroup.get('introducerQuestionnairesop1_8')?.value;
                    const op2 = this.formGroup.get('introducerQuestionnairesop2_8')?.value;
                    return op1 === true || op2 === false;
                }

                if (name === 'introducerQuestionnairesop1_31' || name === 'introducerQuestionnairesop2_31') {
                    if (this.formGroup.get('introducerQuestionnaires_31')?.value !== true) return true;
                    const op1 = this.formGroup.get('introducerQuestionnairesop1_31')?.value;
                    const op2 = this.formGroup.get('introducerQuestionnairesop2_31')?.value;
                    return op1 === true || op2 === false;
                }

                return control.valid;
            });
        }

        return fields.every(name => {
            const control = this.formGroup.get(name);
            return !control || control.valid;
        });
    }


    sectionStatuses = signal<Record<string, 'completed' | 'inprogress' | 'notstarted'>>({
        introducer: 'notstarted', employment: 'notstarted', payment: 'notstarted',
        general: 'notstarted', fitproper: 'notstarted', additional: 'notstarted',
        declaration: 'notstarted'
    });

    private computeSectionStatus(sectionKey: string): 'completed' | 'inprogress' | 'notstarted' {
        if (sectionKey === 'declaration') {
            if (this.sectionSavedState['declaration'] && this.isDeclarationAccepted()) return 'completed';
            if (this.isDeclarationAccepted()) return 'inprogress';
            return 'notstarted';
        }

        // Additional Information is self-contained: completed only if it has
        // real (non-whitespace) content AND has been saved. No dependency on
        // isSectionValid() here, since that method is shared and easy to
        // accidentally regress for this one optional field.
        // Additional Information is optional — status is driven purely by
        // whether the user has clicked Save on this panel, not by content.
        // if (sectionKey === 'additional') {
        //     if (this.isReadOnly()) {
        //         return 'completed';
        //     }
        //     return this.sectionSavedState['additional'] ? 'completed' : 'notstarted';
        // }
        if (sectionKey === 'additional') {
            if (this.isReadOnly() && this.isDeclarationAccepted()) {
                return 'completed';
            }

            // If it has already been saved/completed, keep it completed
            // (Note: setupAutoUnsaveOnChange automatically flips this to false if you type)
            if (this.sectionSavedState['additional']) {
                return 'completed';
            }

            const control = this.formGroup.get('additionalInfo');
            const additionalValue = control?.value;
            const isFilled = this.isFieldFilled(additionalValue);

            // If the user has typed anything or the control is dirty/touched, show inprogress
            if (isFilled || control?.dirty || control?.touched) {
                return 'inprogress';
            }

            return 'notstarted';
        }

        const fields = this.sectionFieldsMap[sectionKey] || [];
        const anyFilled = fields.some(name => {
            const control = this.formGroup.get(name);
            return control && this.isFieldFilled(control.value);
        });

        if (!anyFilled) return 'notstarted';

        return (this.sectionSavedState[sectionKey] && this.isSectionValid(sectionKey))
            ? 'completed'
            : 'inprogress';
    }


    private refreshAllSectionStatuses(): void {
        const next: Record<string, 'completed' | 'inprogress' | 'notstarted'> = {} as any;
        Object.keys(this.sectionFieldsMap).forEach(key => {
            next[key] = this.computeSectionStatus(key);
        });
        // console.log('[refreshAllSectionStatuses] computed:', next);
        // console.log('[refreshAllSectionStatuses] sectionSavedState:', this.sectionSavedState);
        this.sectionStatuses.set(next);
        this.cdr.detectChanges();   // force sync UI update, bypass any zone timing issue
    }

    async onSectionSave(sectionKey: string, silent: boolean = false): Promise<void> {
        const fields = this.sectionFieldsMap[sectionKey] || [];
        const nationality = this.formGroup.get('nationality')?.value;

        const generalConditionalMap: { [key: string]: string } = {
            introducerQuestionnairesyes_1: 'introducerQuestionnaires_1',
            introducerQuestionnairesyes_3: 'introducerQuestionnaires_3',
            introducerQuestionnairesfile_4: 'introducerQuestionnaires_4',
            introducerQuestionnairesyes_5: 'introducerQuestionnaires_5',
            introducerQuestionnairesyes_6: 'introducerQuestionnaires_6'
        };

        fields.forEach(name => {
            if (sectionKey === 'introducer') {
                if (name === 'otherNationality' &&
                    nationality !== 'Singapore Permanent Resident' &&
                    nationality !== 'others') {
                    return;
                }
                if (name === 'address_proof_attachmentfileName' && nationality !== 'others') {
                    return;
                }
            }

            if (sectionKey === 'general' && generalConditionalMap[name]) {
                const parentControl = this.formGroup.get(generalConditionalMap[name]);
                if (parentControl?.value !== true) {
                    return;
                }
            }

            this.formGroup.get(name)?.markAsTouched();
        });

        if (sectionKey === 'additional') {
            // Optional field — clicking Save always marks it completed,
            // regardless of whether the textarea has content or is empty.
            this.sectionSavedState['additional'] = true;
        } else {
            this.sectionSavedState[sectionKey] = this.isSectionValid(sectionKey);
        }

        this.refreshAllSectionStatuses();
        await this.saveSectionToApi(sectionKey, silent);

        if (!silent) {
            const currentIndex = this.sectionOrder.indexOf(sectionKey);
            if (currentIndex !== -1 && currentIndex < this.sectionOrder.length - 1) {
                this.activePanelIndex = currentIndex + 1;
            }
            else { this.activePanelIndex = -1; }
        }

    }

    private isFieldFilled(value: any): boolean {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string') return value.trim().length > 0;
        return true;
    }

    // getSectionStatus(sectionKey: string): 'completed' | 'inprogress' | 'notstarted' {
    //     if (this.isReadOnly()) {
    //         return 'completed';
    //     }

    //     // if (sectionKey === 'declaration') {
    //     //     if (this.isDeclarationAccepted()) {
    //     //         return 'completed';
    //     //     }
    //     //     const declFilled = this.isFieldFilled(this.formGroup.get('declarationaccepted')?.value) ||
    //     //         this.isFieldFilled(this.formGroup.get('date')?.value);
    //     //     return declFilled ? 'inprogress' : 'notstarted';
    //     // }
    //     // Update this block in getSectionStatus
    //     if (sectionKey === 'declaration') {
    //         if (this.isDeclarationAccepted()) return 'completed';

    //         // Check if the user has at least interacted with the checkbox or date
    //         const hasInteracted = this.isFieldFilled(this.formGroup.get('declarationaccepted')?.value) ||
    //             this.isFieldFilled(this.formGroup.get('date')?.value);
    //         return hasInteracted ? 'inprogress' : 'notstarted';
    //     }
    //     const fields = this.sectionFieldsMap[sectionKey] || [];
    //     const anyFilled = fields.some(name => {
    //         const control = this.formGroup.get(name);
    //         return control && this.isFieldFilled(control.value);
    //     });

    //     if (!anyFilled) {
    //         return 'notstarted';
    //     }

    //     // 'additional' has no required fields, so isSectionValid() is trivially
    //     // true even when totally empty — keep it gated on an explicit save,
    //     // same special case your original recompute logic already carved out.
    //     if (sectionKey === 'additional') {
    //         return this.sectionSavedState[sectionKey] ? 'completed' : 'inprogress';
    //     }

    //     return this.isSectionValid(sectionKey) ? 'completed' : 'inprogress';
    // }

    // TEMP debug helper — logs exactly which field(s) are invalid in a section
    private debugSectionValidity(sectionKey: string): void {
        const fields = this.sectionFieldsMap[sectionKey] || [];
        fields.forEach(name => {
            const control = this.formGroup.get(name);
            if (!control) return;
            // console.log(
            //     `[${sectionKey}] ${name}:`,
            //     'value=', control.value,
            //     'valid=', control.valid,
            //     'status=', control.status,
            //     'errors=', control.errors
            // );
        });
    }


    // getSectionStatus(sectionKey: string): 'completed' | 'inprogress' | 'notstarted' {

    //     if (sectionKey === 'declaration') {
    //         if (this.sectionSavedState['declaration'] && this.isDeclarationAccepted()) {
    //             return 'completed';
    //         }
    //         if (this.isDeclarationAccepted()) {
    //             return 'inprogress';
    //         }
    //         return 'notstarted';
    //     }

    //     const fields = this.sectionFieldsMap[sectionKey] || [];
    //     const anyFilled = fields.some(name => {
    //         const control = this.formGroup.get(name);
    //         return control && this.isFieldFilled(control.value);
    //     });

    //     if (!anyFilled) {
    //         return 'notstarted';
    //     }

    //     // Pure read - no assignment here.
    //     return (this.sectionSavedState[sectionKey] && this.isSectionValid(sectionKey))
    //         ? 'completed'
    //         : 'inprogress';
    // }



    getSectionStatus(sectionKey: string): 'completed' | 'inprogress' | 'notstarted' {
        return this.sectionStatuses()[sectionKey] ?? 'notstarted';
    }
    // getSectionStatus(sectionKey: string): 'completed' | 'inprogress' | 'notstarted' {
    //     // if (this.isReadOnly()) {
    //     //     return 'completed';
    //     // }

    //     if (sectionKey === 'declaration') {
    //         if (this.sectionSavedState['declaration'] && this.isDeclarationAccepted()) {
    //             return 'completed';
    //         }
    //         if (this.isDeclarationAccepted()) {
    //             return 'inprogress';
    //         }
    //         return 'notstarted';
    //     }

    //     // Handle Additional Information section status
    //     if (sectionKey === 'additional') {
    //         if (this.sectionSavedState['additional']) {
    //             return 'completed';
    //         }
    //         const additionalValue = this.formGroup.get('additionalInfo')?.value;
    //         return this.isFieldFilled(additionalValue) ? 'inprogress' : 'notstarted';
    //     }

    //     const fields = this.sectionFieldsMap[sectionKey] || [];
    //     const anyFilled = fields.some(name => {
    //         const control = this.formGroup.get(name);
    //         return control && this.isFieldFilled(control.value);
    //     });

    //     if (!anyFilled) {
    //         return 'notstarted';
    //     }

    //     return (this.sectionSavedState[sectionKey] && this.isSectionValid(sectionKey))
    //         ? 'completed'
    //         : 'inprogress';
    // }

    /**
 * The backend is expected to return employment_history as a single object,
 * but if duplicate rows exist (e.g. from the id:0 insert-instead-of-update
 * bug), it comes back as an array instead. Normalize both shapes here so
 * the rest of the code always works with one canonical row - the one with
 * the highest id, since these are auto-incrementing and higher = more recent.
 */
    private getLatestEmploymentHistory(raw: any): any {
        if (!raw) return null;
        if (Array.isArray(raw)) {
            if (raw.length === 0) return null;
            return raw.reduce((latest, item) =>
                (item?.id ?? 0) > (latest?.id ?? 0) ? item : latest
            );
        }
        return raw;
    }

    //   async onFinalSubmit(event?: Event): Promise<void> {
    //     event?.preventDefault();

    //     if (!this.isDeclarationAccepted()) {
    //         return;
    //     }

    //     for (const key of Object.keys(this.sectionFieldsMap)) {
    //         this.onSectionSave(key);
    //     }
    //     this.sectionSavedState['declaration'] = true;

    //     if (this.formGroup.invalid) {
    //         return;
    //     }

    //     this.activePanelIndex = -1;
    //     window.location.reload();
    //     this.isReadOnly.set(true);
    //     this.formGroup.disable();

    //     this.next.emit(this.formGroup.valid);
    //     await this.logFieldNames();
    // }

    async onFinalSubmit(event?: Event): Promise<void> {
        event?.preventDefault();

        if (!this.isDeclarationAccepted()) {
            return;
        }

        const dialogRef = this.dialog.open(ConfirmPopupComponent, {
            width: '500px',
            disableClose: true,
            data: {
                message: 'Are you sure you want to submit this Application?',
                hidecancel: false,
            }
        });

        dialogRef.afterClosed().subscribe(async (result) => {
            if (result?.data === true) {
                await this.proceedFinalSubmit();
            }
            // result.data !== true → user cancelled, do nothing, form stays as-is
        });
    }

    private async proceedFinalSubmit(): Promise<void> {
        this.setSprinner.set(true);

        for (const key of Object.keys(this.sectionFieldsMap)) {
            await this.onSectionSave(key, true);
        }
        this.sectionSavedState['declaration'] = true;

        const allSectionsValid = Object.keys(this.sectionFieldsMap)
            .every(key => this.isSectionValid(key));

        if (!allSectionsValid) {
            this.setSprinner.set(false);
            this.snackBarService.openFailureSnackBar(
                'Some required fields are missing or invalid. Please review the highlighted sections.'
            );
            return;
        }

        // Mark every section completed immediately, in one pass, before the
        // final status refresh — so the badges flip to "Completed" together
        // instead of trickling in one at a time as each onSectionSave() above
        // already triggered its own refreshAllSectionStatuses() call.
        Object.keys(this.sectionFieldsMap).forEach(key => {
            this.sectionSavedState[key] = true;
        });
        this.refreshAllSectionStatuses();

        this.activePanelIndex = -1;
        this.isReadOnly.set(true);
        this.formGroup.disable({ emitEvent: false });
        this.next.emit(true);

        this.setSprinner.set(false);
        // this.showSubmittedAndLogout();
    }

    changeFullName(fullName: string): string {
        return fullName
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    }

    onDeclarationChange(checked: boolean): void {
        const control = this.formGroup.get('declarationaccepted');
        this.refreshAllSectionStatuses();
        control?.setValue(checked);
        control?.markAsDirty();
        control?.markAsTouched();

        if (checked) {
            this.formGroup.get('declarationName')?.setValue(this.formGroup.get('fullName')?.value ?? '');
            this.formGroup.get('date')?.setValue(this.toDateInputValue(new Date().toISOString()));
        } else {
            this.formGroup.get('declarationName')?.reset();
            this.formGroup.get('date')?.reset();
        }

        // console.log('declarationaccepted set to:', control?.value);
        // console.log('isDeclarationAccepted():', this.isDeclarationAccepted());
        // console.log('getSectionStatus:', this.getSectionStatus('declaration'));
    }

    isDeclarationAccepted(): boolean {
        return this.formGroup.get('declarationaccepted')?.value === true;
    }

    // ---------- Question (Yes/No) helpers ----------

    onCheckboxChange(checked: boolean, value: boolean, field: string): void {
        const control = this.formGroup.get(field);
        control?.setValue(checked ? value : null);
        control?.markAsDirty();
        control?.markAsTouched();
    }

    onCheckboxChange1(checked: boolean, value: boolean, field: string, index: number): void {
        const control = this.formGroup.get(field);
        control?.setValue(checked ? value : null);
        control?.markAsDirty();
        control?.markAsTouched();

        if (checked && value === true) {
            if (index === 22 || index === 10 || index === 27 || index === 28) {
                this.formGroup.get('attachment' + index)?.setValidators([Validators.required]);
                this.formGroup.get('attachment' + index)?.updateValueAndValidity();
            } else if (index === 4) {
                this.formGroup.get('introducerQuestionnairesfile_4')?.setValidators([Validators.required]);
                this.formGroup.get('introducerQuestionnairesfile_4')?.updateValueAndValidity();
            } else if (index === 1 || index === 3 || index === 5 || index === 6 || index === 8) {
                this.formGroup.get('introducerQuestionnairesyes_' + index)?.setValidators([Validators.required, noWhitespace]);
                this.formGroup.get('introducerQuestionnairesyes_' + index)?.updateValueAndValidity();
            }
        } else {
            this.formGroup.get('attachment' + index)?.clearValidators();
            this.formGroup.get('attachment' + index)?.reset();
            this.formGroup.get('attachment' + index)?.updateValueAndValidity();

            this.formGroup.get('introducerQuestionnairesyes_' + index)?.clearValidators();
            this.formGroup.get('introducerQuestionnairesyes_' + index)?.reset();
            this.formGroup.get('introducerQuestionnairesyes_' + index)?.updateValueAndValidity();

            if (index === 4) {
                const categoryName = this.DOCUMENT_TYPE_LABELS['gendralinformation_introducer_Questionnaires_file_4'];
                const existing = this.getLatestAttachmentByType(categoryName);

                if (existing) {
                    // A document was actually uploaded to the backend for this
                    // question - go through the real delete flow (confirms with
                    // the user, calls deletedocument(), and removes it from
                    // viewIntroducerDetails) instead of just clearing the form
                    // controls locally, which left the DB record orphaned.
                    this.removeFile('introducerQuestionnairesfile_4', 'gendralinformation_introducer_Questionnaires_file_4', categoryName);
                } else {
                    // Nothing was ever uploaded (e.g. user is toggling before
                    // ever picking a file) - nothing to delete from the DB,
                    // just clear the local controls.
                    this.formGroup.get('introducerQuestionnairesfile_4')?.clearValidators();
                    this.formGroup.get('introducerQuestionnairesfile_4')?.reset();
                    this.formGroup.get('introducerQuestionnairesfile_4')?.updateValueAndValidity();
                    this.formGroup.get('gendralinformation_introducer_Questionnaires_file_4')?.reset();
                }
            }
        }
    }

    /**
  * Question 4's file requirement is normally toggled by onCheckboxChange1()
  * when the user clicks Yes/No live. But loaded answers arrive via
  * patchValue() in patchChecklist(), which never fires that click handler —
  * so a loaded "No" answer left the file control's original
  * Validators.required in place, making the form invalid even though
  * nothing is actually missing. This re-applies the correct validator
  * state based on the value that was just loaded.
  */
    private syncConditionalFileValidator(): void {
        const isYes = this.formGroup.get('introducerQuestionnaires_4')?.value === true;
        const fileControl = this.formGroup.get('introducerQuestionnairesfile_4');

        if (isYes) {
            fileControl?.setValidators([Validators.required]);
        } else {
            fileControl?.clearValidators();
        }
        fileControl?.updateValueAndValidity({ emitEvent: false });
    }



    onPairedCheckboxChange(
        checked: boolean,
        value: boolean,
        yesControl: string,
        noControl: string
    ): void {

        if (!checked) {
            this.formGroup.get(yesControl)?.setValue(null);
            this.formGroup.get(noControl)?.setValue(null);
            return;
        }

        if (value) {
            this.formGroup.patchValue({
                [yesControl]: true,
                [noControl]: null
            });
        } else {
            this.formGroup.patchValue({
                [yesControl]: null,
                [noControl]: false
            });
        }
    }

    setCheckboxValue(checked: boolean, value: boolean, field: string): void {
        const control = this.formGroup.get(field);
        control?.setValue(checked ? value : null);
        control?.markAsDirty();
        control?.markAsTouched();

        if (field === 'introducerQuestionnaires_8' && (!checked || value === false)) {
            this.formGroup.get('introducerQuestionnairesyes_8')?.reset();
            this.formGroup.get('introducerQuestionnairesop1_8')?.reset();
            this.formGroup.get('introducerQuestionnairesop2_8')?.reset();
        }
        if (field === 'introducerQuestionnaires_31' && (!checked || value === false)) {
            this.formGroup.get('introducerQuestionnairesop1_31')?.reset();
            this.formGroup.get('introducerQuestionnairesop2_31')?.reset();
        }
    }

    questionError(field: string): boolean {
        const control = this.formGroup.get(field);
        return !!control && control.invalid && (control.touched || control.dirty);
    }

    pairedQuestionError(yesControl: string, noControl: string): boolean {
        const yes = this.formGroup.get(yesControl);
        const no = this.formGroup.get(noControl);
        const answered = yes?.value === true || no?.value === false;
        const touched = !!(yes?.touched || no?.touched || yes?.dirty || no?.dirty);
        return !answered && touched;
    }

    // ---------- Phone number restriction ----------

    allowNumbersOnly(event: KeyboardEvent): void {
        const charCode = event.which ? event.which : event.keyCode;
        const input = event.target as HTMLInputElement;

        if (charCode === 43) {                         // '+'
            if (input.selectionStart !== 0 || input.value.includes('+')) {
                event.preventDefault();                // only allowed once, only at position 0
            }
            return;
        }

        if (charCode < 48 || charCode > 57) {
            event.preventDefault();
            return;
        }

        if (input.value.length >= this.MAX_PHONE_LENGTH && input.selectionStart === input.selectionEnd) {
            event.preventDefault();
        }
    }

    sanitizeNumericInput(event: Event, controlName: string): void {
        const input = event.target as HTMLInputElement;
        const hasLeadingPlus = input.value.startsWith('+');
        const digits = input.value.replace(/\D/g, '');
        let sanitized = hasLeadingPlus ? '+' + digits : digits;

        if (sanitized.length > this.MAX_PHONE_LENGTH) {
            sanitized = sanitized.slice(0, this.MAX_PHONE_LENGTH);
        }

        if (sanitized !== input.value) {
            input.value = sanitized;
        }
        this.formGroup.get(controlName)?.setValue(sanitized);
    }

    // ---------- Attachments ----------

    onAttachmentFileChange(event: any, controlName: string, fileVar: string, categoryName: string): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        const control = this.formGroup.get(controlName);

        this.formGroup.patchValue({
            [fileVar]: file,
            [controlName]: file?.name
        });

        if (!control) return;

        control.markAsTouched();
        control.setErrors(null);

        if (!file) {
            control.setValue(null);
            return;
        }

        if (!this.ALLOWED_FILE_TYPES.has(file.type)) {
            control.setErrors({ invalidType: true });
            return;
        }

        if (file.size > this.MAX_FILE_SIZE) {
            control.setErrors({ maxSize: true });
            return;
        }

        control.updateValueAndValidity();
        this.uploaddocument(fileVar, controlName, categoryName);
    }

    /**
     * Sends the human-readable document type label (matching what's listed
     * in DOCUMENT_TYPE_LABELS / the onboardingDocumentType values returned by
     * the GET) as Description, so the backend can identify document types
     * consistently between upload and delete. "Other Attachments" rows all go
     * up as 'OtherAttachments' with their row index in ResponseValue.
     */
    async uploaddocument(fileVar: string, controlName: string, categoryName: string): Promise<void> {
        // If a document already exists for this slot, delete it first so
        // replacing a file doesn't leave an orphaned duplicate row in the DB.
        const existing = this.getLatestAttachmentByType(categoryName);
        if (existing) {
            const { onboardingId, docTypeId, id } = existing;
            const deleteResponse = await this.newRepApplicationService
                .deletedocument({ onboardingId, docTypeId, id })
                .then((response) => JSON.parse(response))
                .catch((error) => {
                    console.error('Failed to delete previous attachment before re-upload', error);
                    return null;
                });

            if (deleteResponse?.status !== HttpStatusCode.Ok) {
                this.snackBarService.openFailureSnackBar('We couldn\'t replace the attachment. Please try again');
                return;
            }

            this.viewIntroducerDetails = this.viewIntroducerDetails.filter(item => item !== existing);
        }

        const isOtherAttachment = this.isOtherAttachmentKey(categoryName);
        const formData = new FormData();
        formData.append('file', this.formGroup.get(fileVar)?.value);
        formData.append('Description', isOtherAttachment ? this.otherAttachmentDescription : categoryName);
        formData.append('filename', this.formGroup.get(fileVar)?.value?.name);
        formData.append('ResponseValue', isOtherAttachment ? this.otherAttachmentIndex(categoryName) : '');

        const responseData = await this.newRepApplicationService
            .uploadDocuments(formData)
            .then((response) => JSON.parse(response))
            .catch((error) => error);

        if (responseData.status === HttpStatusCode.Ok) {
            this.formGroup.patchValue({
                [controlName]: responseData.data.responsedata
            });
            await this.refreshAttachments();
            this.snackBarService.openSuccessSnackBar('document uploaded successfully!');
        } else if (responseData.code === HttpStatusCode.BadRequest) {
            this.snackBarService.openFailureSnackBar('We couldn\'t process your request. Please try again');
        }
    }

    /**
     * Removes a previously uploaded attachment.
     */
    removeFile(controlName: string, fileVar: string, categoryName: string): void {
        const dialogRef = this.dialog.open(ConfirmPopupComponent, {
            width: '500px',
            disableClose: true,
            data: {
                message: 'Do you want to remove the attachement?',
                hidecancel: false,
            }
        });

        dialogRef.afterClosed().subscribe(async (result) => {
            if (result?.data !== true) {
                return;
            }

            const match = this.getLatestAttachmentByType(categoryName);

            if (match) {
                const { onboardingId, docTypeId, id } = match;

                const responseData = await this.newRepApplicationService
                    .deletedocument({ onboardingId, docTypeId, id })
                    .then((response) => JSON.parse(response))
                    .catch((error) => {
                        console.error('Failed to delete document', error);
                        return null;
                    });

                // Only clear the UI if the backend actually confirms the delete -
                // otherwise the file would look "removed" here but still exist
                // in the DB, and reappear the next time the form is reloaded.
                if (responseData?.status !== HttpStatusCode.Ok) {
                    this.snackBarService.openFailureSnackBar('We couldn\'t remove the attachment. Please try again');
                    return;
                }

                this.viewIntroducerDetails = this.viewIntroducerDetails.filter(item => item !== match);
            }

            this.formGroup.get(controlName)?.reset();
            const fileInput = document.getElementById(controlName) as HTMLInputElement;
            if (fileInput) {
                fileInput.value = '';
            }
            this.formGroup.get(fileVar)?.reset();

            if (controlName === 'introducerQuestionnairesfile_4') {
                this.syncConditionalFileValidator();   // re-applies `required` only if Q4 is still "Yes"
            }
            this.formGroup.controls[controlName].markAsTouched();
            this.formGroup.controls[controlName].updateValueAndValidity();
        });
    }

    // ---------- Signature ----------

    openSignatureDialog(): void {
        const dialogRef = this.dialog.open(SignatureDialogComponent, { width: '500px', disableClose: true });
        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.formGroup.controls['signature'].setValue(result);
            }
        });
    }

    get signatureValue() {
        return this.formGroup.controls['signature'].value;
    }

    clearSignature(): void {
        this.formGroup.controls['signature'].setValue(null);
        this.formGroup.controls['signature'].markAsTouched();
    }

    // ---------- Dropdown data ----------

    async getDropdownOptions(): Promise<void> {
        const responseData = await this.newRepApplicationService
            .getDropdownOptions()
            .then((response) => JSON.parse(response))
            .catch((error) => error);

        if (responseData?.status === HttpStatusCode.Ok) {
            this.nationalities = responseData.data.responsedata.constants.nationality_list;
            this.countries = responseData.data.responsedata.constants.country_list;
        }
    }

    // private setupNationalityValidators(): void {
    //     this.formGroup.get('nationality')?.valueChanges.subscribe((value) => {
    //         const otherNationalityControl = this.formGroup.get('otherNationality');
    //         const addressProofControl = this.formGroup.get('address_proof_attachmentfileName');
    //         const previousValue = this.previousNationality;
    //         this.previousNationality = value;

    //         // ---- "Choose nationality" dropdown ----
    //         if (value === 'Singapore Citizen') {
    //             otherNationalityControl?.clearValidators();
    //             if (!otherNationalityControl?.value) {
    //                 otherNationalityControl?.setValue(this.getDefaultCitizenNationalityValue(), { emitEvent: false });
    //             }
    //             otherNationalityControl?.disable({ emitEvent: false });
    //         } else if (value === 'Singapore Permanent Resident' || value === 'others') {
    //             otherNationalityControl?.enable({ emitEvent: false });
    //             otherNationalityControl?.setValidators([Validators.required]);
    //             // Only clear the auto-filled "Singapore Citizen" default - never
    //             // wipe a genuinely loaded PR/Others value.
    //             if (previousValue === 'Singapore Citizen') {
    //                 otherNationalityControl?.reset();
    //             }
    //         } else {
    //             otherNationalityControl?.enable({ emitEvent: false });
    //             otherNationalityControl?.clearValidators();
    //         }
    //         otherNationalityControl?.updateValueAndValidity({ emitEvent: false });

    //         // ---- Address proof attachment: always visible, mandatory only for "others" ----
    //         if (value === 'others') {
    //             addressProofControl?.setValidators([Validators.required]);
    //         } else {
    //             addressProofControl?.clearValidators();
    //         }
    //         addressProofControl?.updateValueAndValidity();
    //     });
    // }

    private setupNationalityValidators(): void {
        this.formGroup.get('nationality')?.valueChanges.subscribe((value) => {
            if (this.isReadOnly()) {
                return;
            }

            const otherNationalityControl = this.formGroup.get('otherNationality');
            const addressProofControl = this.formGroup.get('address_proof_attachmentfileName');
            const previousValue = this.previousNationality;
            this.previousNationality = value;

            if (value === 'Singapore Citizen') {
                otherNationalityControl?.clearValidators();
                // ALWAYS overwrite — don't leave a stale PR/Others value behind
                otherNationalityControl?.setValue(this.getDefaultCitizenNationalityValue(), { emitEvent: false });
                otherNationalityControl?.disable({ emitEvent: false });
            } else if (value === 'Singapore Permanent Resident' || value === 'others') {
                otherNationalityControl?.enable({ emitEvent: false });
                otherNationalityControl?.setValidators([Validators.required]);
                if (previousValue === 'Singapore Citizen') {
                    otherNationalityControl?.reset();
                }
            } else {
                otherNationalityControl?.enable({ emitEvent: false });
                otherNationalityControl?.clearValidators();
            }
            otherNationalityControl?.updateValueAndValidity({ emitEvent: false });

            if (value === 'others') {
                addressProofControl?.setValidators([Validators.required]);
            } else {
                addressProofControl?.clearValidators();
            }
            addressProofControl?.updateValueAndValidity();
        });
    }
    /**
     * Best-effort lookup of the "Singapore" option from the loaded nationalities
     * dropdown data, used to auto-fill the (now disabled) nationality field when
     * "Singapore Citizen" is selected. Falls back to a literal string if the
     * dropdown data hasn't loaded yet (e.g. this fires before getDropdownOptions()
     * resolves) - reconcileDropdownSelections() corrects it once data is in.
     */
    private getDefaultCitizenNationalityValue(): string {
        if (Array.isArray(this.nationalities)) {
            const match = this.nationalities.find(n =>
                String(n.name).toLowerCase().includes('singapore') ||
                String(n.value).toLowerCase().includes('singapore')
            );
            if (match) return match.value;
        }
        return 'Singapore';
    }

    //    private setupAutoUnsaveOnChange(): void {
    //         Object.keys(this.sectionFieldsMap).forEach(sectionKey => {
    //             const fields = this.sectionFieldsMap[sectionKey];
    //             fields.forEach(fieldName => {
    //                 const control = this.formGroup.get(fieldName);
    //                 control?.valueChanges.subscribe(() => {
    //                     // Ignore programmatic value changes fired while the initial
    //                     // data load is still patching/settling the form — only
    //                     // real user edits after load should mark a section unsaved.
    //                     if (!this.detailsLoaded) {
    //                         return;
    //                     }
    //                     if (this.sectionSavedState[sectionKey]) {
    //                         this.sectionSavedState[sectionKey] = false;
    //                         this.refreshAllSectionStatuses();
    //                     }
    //                 });
    //             });
    //         });
    //     }

    private setupAutoUnsaveOnChange(): void {
        Object.keys(this.sectionFieldsMap).forEach(sectionKey => {
            const fields = this.sectionFieldsMap[sectionKey];
            fields.forEach(fieldName => {
                const control = this.formGroup.get(fieldName);
                control?.valueChanges.subscribe(() => {
                    // Ignore programmatic value changes fired while the initial
                    // data load is still patching/settling the form
                    if (!this.detailsLoaded) {
                        return;
                    }
                    if (this.sectionSavedState[sectionKey]) {
                        this.sectionSavedState[sectionKey] = false;
                    }
                    // ADD THIS LINE: Force status signal update on every keystroke/change
                    this.refreshAllSectionStatuses();
                });
            });
        });
    }

    public nricrequired(): boolean {
        return true;
    }

    async logFieldNames(): Promise<void> {
        try {
            const existingPdfBytes = await fetch('assets/images/introducer.pdf').then(res => res.arrayBuffer());
            const pdfDoc = await PDFDocument.load(existingPdfBytes);
            const form = pdfDoc.getForm();
            const fields = form.getFields();
        } catch (error) {
            console.error('logFieldNames failed - PDF template may be missing', error);
        }
    }

    async previewAttachment(controlName: string, categoryName: string) {
        const match = this.getLatestAttachmentByType(categoryName);
        this.showPreview = true;
        if (match) {
            const { id } = match;
            // this.previewPdf(file.url);
            // console.log(id)

            try {

                const responseData = await this.newRepApplicationService
                    .previewdocuments(id);

                // Create blob URL
                const fileURL = URL.createObjectURL(responseData);

                // Safe URL
                this.selectedDocument = this.sanitizer.bypassSecurityTrustResourceUrl(fileURL);
                this.isPdf = this.formGroup.get(controlName)?.value.toLowerCase().endsWith('.pdf');
                this.currentFileName = this.formGroup.get(controlName)?.value;

            } catch (error) {
                console.error(error);
            }
            this.resetZoom();
        }
    }


    zoomIn(): void {
        this.zoomLevel = Math.min(this.MAX_ZOOM, +(this.zoomLevel + this.ZOOM_STEP).toFixed(2));
    }

    zoomOut(): void {
        this.zoomLevel = Math.max(this.MIN_ZOOM, +(this.zoomLevel - this.ZOOM_STEP).toFixed(2));
    }

    resetZoom(): void {
        this.zoomLevel = 1;
    }

    closePreview() {
        this.showPreview = false;
        this.resetZoom();
    }

}